import { Box, SimpleGrid, Stack, Title, Chip, Group } from "@mantine/core";
import { useMemo, useState } from "react";
import { releases, isComingSoon, isReleased } from "@/content/releases";
import { ReleaseCard } from "@/components/release/ReleaseCard";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHero } from "@/components/layout/PageHero";
import { PageContainer } from "@/components/layout/PageContainer";

const PRESET_LABELS = ["All", "mau5trap", "Monstercat", "Self-released"] as const;

// Display order is the array order from src/content/releases.ts — hand-curated
// so the spotlight + strongest tracks lead each section. Don't re-sort here.

export default function ReleasesIndex() {
  const dataLabels = useMemo(
    () => Array.from(new Set(releases.map((r) => r.label))),
    [],
  );
  const labels = useMemo(
    () => [
      "All",
      ...PRESET_LABELS.filter((l) => l !== "All" && dataLabels.includes(l)),
      ...dataLabels.filter((l) => !PRESET_LABELS.includes(l as (typeof PRESET_LABELS)[number])),
    ],
    [dataLabels],
  );
  const [active, setActive] = useState<string>("All");

  const filtered = useMemo(
    () => (active === "All" ? releases : releases.filter((r) => r.label === active)),
    [active],
  );

  const comingSoon = filtered.filter(isComingSoon);
  const released = filtered.filter(isReleased);

  return (
    <>
      <PageHero title="Releases" subtitle="Full 7MD discography." />
      <PageContainer navOffset={false}>
        <Stack gap="xl">
          {labels.length > 1 && (
            <Group gap="xs">
              {labels.map((label) => (
                <Chip
                  key={label}
                  checked={active === label}
                  onChange={() => setActive(label)}
                  color="indigo"
                  variant="light"
                >
                  {label}
                </Chip>
              ))}
            </Group>
          )}

          {comingSoon.length === 0 && released.length === 0 && (
            <EmptyState title="No releases match this filter" />
          )}

          {comingSoon.length > 0 && (
            <Box>
              <Title order={2} size="h3" mb="sm">
                coming soon
              </Title>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="md">
                {comingSoon.map((r) => (
                  <ReleaseCard key={r.slug} release={r} />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {released.length > 0 && (
            <Box>
              <Title order={2} size="h3" mb="sm">
                released
              </Title>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="md">
                {released.map((r) => (
                  <ReleaseCard key={r.slug} release={r} />
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Stack>
      </PageContainer>
    </>
  );
}
